/* ============================================================================
   Territorio. Dónde se está encareciendo la comida.

   La canasta en pesos existe sólo para el país, en ámbito rural y urbano: no hay
   una canasta oficial por estado. Lo que sí hay por territorio es el índice de
   precios de los alimentos, en las 32 entidades y en las 55 ciudades de la
   muestra del INPC.

   Y de ahí sale la regla que gobierna toda esta pantalla: un índice mide CAMBIO,
   no NIVEL. Aquí no se imprime nunca el valor del índice de un lugar junto al de
   otro, ni se traduce a pesos. Todo lo que se compara son variaciones: contra el
   año pasado y contra julio de 2018, que es el único mes en que las 88 series
   existen a la vez. La advertencia va arriba, no en una nota al pie, porque es
   la lectura equivocada más fácil de hacer.
   ========================================================================== */

import { useMemo, useState } from 'react'
import { BarList, type BarRow } from '../charts/BarList'
import { LineChart } from '../charts/LineChart'
import { Segmented } from '../components/Segmented'
import { Sheet } from '../components/Sheet'
import { Callout, Card, FilterBar, FilterField, Section, Select } from '../components/ui'
import { Delta } from '../components/Delta'
import { dataset } from '../data/dataset'
import {
  PLACE_SORTS,
  changeOver,
  TERRITORY_BASE_PERIOD,
  placeChanges,
  placeMetric,
  placeSeriesIndex,
  rebased,
  sortPlaceChanges,
  territorySummary,
  type PlaceChange,
  type PlaceHorizon,
  type PlaceSort,
} from '../data/derive'
import { PLACE_INDEX_WARNING, type PlaceKind } from '../data/schema'
import {
  formatPeriodTitle,
  number,
  percent,
  percentDelta,
  pluralize,
  shortenLabel,
} from '../lib/format'

type Escala = Extract<PlaceKind, 'entidad' | 'ciudad'>

const ESCALA_OPTIONS: readonly { value: Escala; label: string; hint: string }[] = [
  { value: 'entidad', label: 'Estados', hint: 'Las 32 entidades federativas' },
  { value: 'ciudad', label: 'Ciudades', hint: 'Las 55 ciudades de la muestra del INPC' },
]

/** Cómo se nombra el conjunto, para que los rótulos concuerden en género. */
const ESCALA_NOUN: Record<Escala, { one: string; many: string; art: string }> = {
  entidad: { one: 'estado', many: 'estados', art: 'los' },
  ciudad: { one: 'ciudad', many: 'ciudades', art: 'las' },
}

export function Territorio() {
  const [escala, setEscala] = useState<Escala>('entidad')
  const [sort, setSort] = useState<PlaceSort>('sube')
  const [horizonte, setHorizonte] = useState<PlaceHorizon>('anual')
  const [selected, setSelected] = useState<string | null>(null)

  const rows = useMemo(() => placeChanges(dataset, escala), [escala])
  const summary = useMemo(() => territorySummary(rows, dataset, horizonte), [rows, horizonte])
  const ordered = useMemo(
    () => sortPlaceChanges(rows, sort, horizonte),
    [rows, sort, horizonte],
  )

  const noun = ESCALA_NOUN[escala]
  /** El horizonte que NO dibuja la barra, y que por eso va en la columna de valor. */
  const otroHorizonte: PlaceHorizon = horizonte === 'anual' ? 'acumulada' : 'anual'
  const alimentosIndex = useMemo(() => placeSeriesIndex(dataset, 'alimentos'), [])

  const barRows = useMemo<BarRow[]>(
    () =>
      ordered.map((r) => ({
        id: r.place.id,
        label: r.place.name,
        sublabel:
          r.place.kind === 'ciudad' && r.place.stateName ? r.place.stateName : undefined,
        /* La columna de valor lleva SIEMPRE el otro horizonte, para que el
           renglón cuente las dos cosas y la barra no sea la única cifra. */
        value: placeMetric(r, otroHorizonte)?.percent ?? Number.NaN,
        magnitude: placeMetric(r, horizonte)?.percent ?? null,
      })),
    [ordered, horizonte, otroHorizonte],
  )

  const selectedRow = selected ? rows.find((r) => r.place.id === selected) : undefined

  /* Ambas curvas rebasadas a 100 en julio de 2018: es lo que las hace
     comparables y lo que convierte el eje en «cambio desde esa fecha». */
  const chartSeries = useMemo(() => {
    if (!selectedRow) return []
    const nacional = summary.nationalPlace
      ? alimentosIndex.get(summary.nationalPlace.id)
      : undefined
    const out = [
      {
        id: selectedRow.place.id,
        label: selectedRow.place.name,
        /* `shortLabel` se imprime SÓLO al final de la curva, dentro del SVG y con
           un margen fijo: ahí un nombre como «Zona Metropolitana de la Ciudad de
           México» se cortaba a media palabra. La leyenda, el tooltip y la tabla
           espejo usan `label` —el nombre completo—, que es donde no hay límite de
           ancho y donde recortar sí escondería información. */
        shortLabel: shortenLabel(selectedRow.place.name),
        color: 'var(--series-1)',
        points: rebased(selectedRow.points, TERRITORY_BASE_PERIOD),
      },
    ]
    if (nacional) {
      out.push({
        id: 'nacional',
        label: 'Nacional',
        shortLabel: 'Nacional',
        color: 'var(--series-2)',
        points: rebased(nacional.points, TERRITORY_BASE_PERIOD),
      })
    }
    return out
  }, [selectedRow, alimentosIndex, summary.nationalPlace])

  /**
   * Variación ANUAL nacional, calculada aparte de `summary`.
   *
   * `summary.national` sigue al horizonte activo, y eso es correcto para las
   * tarjetas y el callout. La nota de la hoja, en cambio, siempre habla del
   * último año, porque `gapVsNational` es una brecha anual por definición. Al
   * leer de `summary` con el horizonte en «Desde 2018», esa nota decía «en el
   * país subieron 65.1 %» junto a una brecha de 4.0 puntos: dos ventanas
   * distintas en la misma oración.
   */
  const nacionalAnual = useMemo(() => {
    const serie = summary.nationalPlace ? alimentosIndex.get(summary.nationalPlace.id) : undefined
    return serie ? changeOver(serie.points, 12) : null
  }, [alimentosIndex, summary.nationalPlace])

  const periodoAnual = nacionalAnual ? formatPeriodTitle(nacionalAnual.to.period).toLowerCase() : ''
  const periodoTitulo = summary.period ? formatPeriodTitle(summary.period).toLowerCase() : ''
  const baseTitulo = formatPeriodTitle(TERRITORY_BASE_PERIOD).toLowerCase()

  const horizonteOptions = [
    { value: 'anual' as PlaceHorizon, label: 'Último año', hint: 'Contra el mismo mes del año anterior' },
    { value: 'acumulada' as PlaceHorizon, label: 'Desde 2018', hint: `Acumulado desde ${baseTitulo}` },
  ]

  /* Cuenta los que faltan EN EL HORIZONTE ACTIVO: son los que quedan sin barra,
     y anunciar los del otro horizonte contradiría lo que se ve en la tabla. */
  const sinDato = ordered.filter((r) => placeMetric(r, horizonte) === null).length

  /* Cifras de los extremos, en el horizonte activo. Se leen una vez porque de
     ellas dependen tanto el número como el rótulo que lo nombra. */
  const maxPct = summary.highest ? (placeMetric(summary.highest, horizonte)?.percent ?? 0) : 0
  const minPct = summary.lowest ? (placeMetric(summary.lowest, horizonte)?.percent ?? 0) : 0

  const ventanaFrase =
    horizonte === 'anual' ? 'en los últimos doce meses' : `acumulado desde ${baseTitulo}`

  /**
   * ¿Hay algún lugar con variación negativa?
   *
   * Decide la codificación de la barra, y no es cosmética. La barra divergente
   * ancla en cero y gasta media caja en el lado que no se usa: con el acumulado
   * desde 2018, donde los 32 estados van de +54 % a +77 %, las barras salían
   * todas largas y casi iguales y la diferencia entre el primero y el último —23
   * puntos porcentuales, la señal completa de la pantalla— quedaba comprimida en
   * un tercio del ancho. Sin valores negativos, la polaridad no aporta nada y
   * medir desde cero recupera todo el espacio para lo que sí distingue.
   */
  const hayBajas = barRows.some((r) => r.magnitude !== null && r.magnitude < 0)

  /** Brecha contra el nacional, siempre nombrada en puntos porcentuales. */
  const brechaFrase = (row: PlaceChange): string => {
    const propio = placeMetric(row, horizonte)
    if (!propio || !summary.national) return 'sin comparación con el nacional'
    if (propio.to.period !== summary.national.to.period) {
      return 'sin comparación con el nacional'
    }
    const brecha = propio.percent - summary.national.percent
    const magnitud = number(Math.abs(brecha), 1)
    if (Math.abs(brecha) < 0.05) return 'igual que el país'
    return `${magnitud} puntos porcentuales ${brecha > 0 ? 'sobre' : 'bajo'} el país`
  }

  return (
    <>
      <header className="view-head">
        <p className="t-micro view-head__eyebrow">Territorio</p>
        <h1 className="t-title1">Dónde se está encareciendo la comida</h1>
        <p className="t-body ink-2">
          El costo de la canasta en pesos se publica sólo para el país. Lo que el INEGI sí publica
          con detalle territorial es el índice de precios de los alimentos: cuánto subieron en cada
          una de las 32 entidades y en las 55 ciudades donde levanta precios. Para una red de bancos
          repartidos por el país, es la diferencia entre saber que la comida subió y saber dónde.
        </p>
      </header>

      <Callout kind="warning" title="Esto mide cambio, no nivel">
        <p>
          {PLACE_INDEX_WARNING} Que en Jalisco los alimentos hayan subido más que en Chiapas no
          significa que en Jalisco sean más caros: significa que ahí se encarecieron más rápido.
        </p>
        <p>
          No existe una canasta alimentaria oficial por estado ni por ciudad, así que esta pantalla
          nunca convierte estos índices a pesos. La cifra en pesos, nacional y por ámbito, está en
          Panorama.
        </p>
        <p>
          {/* La serie se llama «alimentos» en todos los rótulos por brevedad, pero
              el agregado que publica el INEGI por territorio incluye alcohol y
              tabaco. Decirlo aquí y no sólo al pie evita que la cifra se lea como
              si fuera la canasta alimentaria medida por estado. */}
          El índice territorial es el objeto del gasto «Alimentos, bebidas y tabaco» del INPC: es
          el único que el INEGI desglosa por entidad y por ciudad, e incluye bebidas alcohólicas y
          tabaco. No es la canasta alimentaria, que sí los excluye.
        </p>
      </Callout>

      <FilterBar ariaLabel="Filtros del desglose territorial">
        <FilterField label="Escala">
          <Segmented
            options={ESCALA_OPTIONS}
            value={escala}
            onChange={(value) => {
              setEscala(value)
              setSelected(null)
            }}
            ariaLabel="Escala territorial"
          />
        </FilterField>
        <FilterField label="Comparar contra">
          <Segmented
            options={horizonteOptions}
            value={horizonte}
            onChange={setHorizonte}
            ariaLabel="Horizonte de comparación"
          />
        </FilterField>
        <FilterField label="Ordenar por">
          <Select value={sort} onChange={setSort} options={PLACE_SORTS} ariaLabel="Ordenar por" />
        </FilterField>
      </FilterBar>

      {summary.national && summary.highest && summary.lowest ? (
        <div className="grid grid--3">
          <Card>
            <p className="t-micro ink-3">Referencia nacional</p>
            <p className="t-title2 highlight-name">Todo el país</p>
            <div className="row">
              <Delta value={summary.national.percent} size="md" />
              <span className="t-footnote ink-2">{ventanaFrase}</span>
            </div>
          </Card>
          <Card>
            {/* El rótulo sigue al signo: con una variación negativa, «donde más
                subió» seguiría siendo cierto, pero «donde menos subió» sobre un
                −1.4 % afirma una subida que no ocurrió. */}
            <p className="t-micro ink-3">{maxPct >= 0 ? 'Donde más subió' : 'Donde menos bajó'}</p>
            <p className="t-title2 highlight-name">{summary.highest.place.name}</p>
            <div className="row">
              <Delta value={maxPct} size="md" />
              <span className="t-footnote ink-2">{brechaFrase(summary.highest)}</span>
            </div>
          </Card>
          <Card>
            <p className="t-micro ink-3">{minPct < 0 ? 'Donde más bajó' : 'Donde menos subió'}</p>
            <p className="t-title2 highlight-name">{summary.lowest.place.name}</p>
            <div className="row">
              <Delta value={minPct} size="md" />
              <span className="t-footnote ink-2">{brechaFrase(summary.lowest)}</span>
            </div>
          </Card>
        </div>
      ) : null}

      {summary.spread !== null && summary.national ? (
        <Callout
          kind="info"
          title={`${number(summary.spread, 1)} puntos porcentuales separan a los extremos`}
        >
          <p>
            {/* El sujeto es «la inflación» en las dos ramas, para que concuerde
                con el «va de» que sigue. */}
            {horizonte === 'anual'
              ? `En ${periodoTitulo}, la inflación de alimentos fue de `
              : `Desde ${baseTitulo}, la inflación acumulada de alimentos es de `}
            {percentDelta(summary.national.percent)} en el país, pero va de{' '}
            {percentDelta(minPct)} en {summary.lowest!.place.name} a {percentDelta(maxPct)} en{' '}
            {summary.highest!.place.name}. {summary.aboveNational} de {summary.total} {noun.many}{' '}
            quedaron por encima del promedio nacional
            {summary.excluded > 0
              ? `; ${summary.excluded} ${pluralize(summary.excluded, noun.one, noun.many)} quedaron fuera de esta cuenta por no cerrar en el mismo mes`
              : ''}
            .
          </p>
          <p>
            {/* Antes esta frase afirmaba «al doble de velocidad» sin calcular nada,
                y con las cifras de hoy es falsa: entre los extremos hay un factor
                de 1.3, no de 2. La comparación se deriva del dato o no se hace. Y
                el cociente sólo tiene sentido con los dos extremos positivos: si el
                bajo es negativo no hay «tantas veces», hay un lugar que subió y
                otro que bajó, que es una frase distinta. */}
            Un promedio nacional esconde esa dispersión:{' '}
            {minPct > 0
              ? `en ${summary.highest!.place.name} la comida se encareció ${number(maxPct / minPct, 1)} veces más rápido que en ${summary.lowest!.place.name}`
              : `en ${summary.highest!.place.name} la comida se encareció mientras que en ${summary.lowest!.place.name} se abarató`}
            , aunque la cifra en pesos sólo exista para todo el país.
          </p>
        </Callout>
      ) : null}

      <Section
        title={
          horizonte === 'anual'
            ? `Inflación de alimentos en el último año, por ${noun.one}`
            : `Inflación de alimentos acumulada desde ${baseTitulo}, por ${noun.one}`
        }
        description={
          hayBajas
            ? 'Barra a la derecha y naranja: los alimentos subieron. A la izquierda y azul: bajaron. Cero al centro.'
            : /* La frase nombra la VENTANA ACTIVA, no el acumulado. El modo de barra
                 lo decide `hayBajas`, que se calcula sobre el horizonte activo: en
                 la mayoría de los meses ninguna entidad baja en el año, así que este
                 texto se imprime también bajo el título «en el último año». Decir
                 «desde julio de 2018» ahí ponía dos ventanas distintas en el mismo
                 bloque. */
              `Ninguno bajó ${horizonte === 'anual' ? 'en el último año' : `desde ${baseTitulo}`}, así que aquí la barra mide cuánto subió y arranca en cero, sin lado negativo que partiría el ancho a la mitad.`
        }
      >
        <Card>
          <BarList
            rows={barRows}
            title={`${barRows.length} ${pluralize(barRows.length, noun.one, noun.many)}`}
            subtitle={`Objeto del gasto «Alimentos, bebidas y tabaco» del INPC, a ${periodoTitulo}. Toque un renglón para ver su curva contra la nacional.`}
            nameHeading={escala === 'entidad' ? 'Estado' : 'Ciudad'}
            caption={`Variación de los precios de alimentos por ${noun.one}, en el último año y desde ${baseTitulo}.`}
            valueHeading={horizonte === 'anual' ? `Desde ${baseTitulo}` : 'Último año'}
            magnitudeHeading={horizonte === 'anual' ? 'Cambio anual' : `Desde ${baseTitulo}`}
            formatValue={(value) => (Number.isNaN(value) ? 'sin dato' : percentDelta(value))}
            mode={hayBajas ? 'diverging' : 'magnitude'}
            /* Naranja también sin lado negativo: son alzas, y el azul de la barra
               de magnitud es el mismo tono con el que esta app dice «bajó». */
            magnitudeTone="alza"
            magnitudeKind="percent"
            valueKind="percent"
            onRowClick={(id) => setSelected(id)}
            footnote="Fuente: INEGI, Índice Nacional de Precios al Consumidor por entidad federativa y por ciudad, base segunda quincena de julio de 2018 = 100."
          />
        </Card>
      </Section>

      {sinDato > 0 ? (
        <Callout
          kind="warning"
          title={`${sinDato} ${pluralize(sinDato, noun.one, noun.many)} sin esta comparación`}
        >
          <p>
            {horizonte === 'anual'
              ? 'Su serie no llega a trece meses, así que no hay contra qué comparar el último año.'
              : `Su serie no alcanza ${baseTitulo}, así que no hay contra qué medir el acumulado.`}{' '}
            Se listan al final, sin barra, en vez de dibujarles un cero.
          </p>
        </Callout>
      ) : null}

      <Callout kind="info" title="Por qué las ciudades no son un mapa">
        <p>
          El INPC se levanta en 55 ciudades, no en todo el territorio: son la muestra con la que se
          calcula el índice nacional. Una ciudad representa a su zona urbana, no al estado entero, y
          hay estados con varias ciudades en la muestra y otros con una sola. Por eso aquí se
          ordenan y se comparan, pero no se pintan sobre un mapa: colorear un estado completo con el
          dato de una ciudad diría algo que la fuente no dice.
        </p>
      </Callout>

      <Sheet
        open={selectedRow !== undefined}
        onClose={() => setSelected(null)}
        title={selectedRow?.place.name ?? ''}
        subtitle={
          selectedRow
            ? selectedRow.place.kind === 'ciudad' && selectedRow.place.stateName
              ? `${selectedRow.place.stateName} · precios de alimentos contra el nacional`
              : 'Precios de alimentos contra el nacional'
            : ''
        }
      >
        {selectedRow ? (
          <div className="stack">
            <div className="grid grid--2">
              <Card>
                <p className="t-micro ink-3">Último año</p>
                {selectedRow.annual ? (
                  <Delta value={selectedRow.annual.percent} size="md" />
                ) : (
                  <p className="t-body ink-3">Sin dato</p>
                )}
              </Card>
              <Card>
                <p className="t-micro ink-3">Desde {baseTitulo}</p>
                {selectedRow.sinceBase ? (
                  <Delta value={selectedRow.sinceBase.percent} size="md" />
                ) : (
                  <p className="t-body ink-3">Sin dato</p>
                )}
              </Card>
            </div>

            <LineChart
              series={chartSeries}
              title="Precios de alimentos desde julio de 2018"
              /* «Puntos porcentuales», no «por ciento»: rebasadas, cada curva vale
                 100·(1+variación acumulada), así que la separación vertical es la
                 RESTA de dos variaciones. Leerla como un por ciento del nacional
                 infla la cifra —Jalisco está 10.6 puntos arriba, no 10.6 % arriba,
                 que serían 6.4 %— y empuja justo a la lectura de nivel que el aviso
                 de arriba prohíbe. */
              subtitle={`Las dos curvas arrancan en 100 en ${baseTitulo}. Lo que las separa son los puntos porcentuales que este lugar acumuló de más o de menos que el país desde esa fecha.`}
              formatValue={(value) => number(value, 1)}
              unitLabel={`índice, ${baseTitulo} = 100`}
              height={280}
              hRules={[{ value: 100, label: baseTitulo }]}
              footnote="Fuente: INEGI, INPC. Series reexpresadas con base 100 en julio de 2018 para poder compararlas."
            />

            {selectedRow.gapVsNational !== null && selectedRow.annual && nacionalAnual ? (
              <p className="t-footnote ink-2">
                {/* Tres cifras distintas, cada una con su nombre: lo que hizo el
                    lugar, lo que hizo el país y la distancia entre ambos. Antes el
                    verbo salía del signo del lugar pero la magnitud salía de la
                    BRECHA, así que un −1.4 % con 4.0 puntos de brecha se leía como
                    «bajaron 4.0», que no es ninguna de las dos cosas. La ventana se
                    nombra aquí porque esta nota siempre habla del último año, esté
                    donde esté el selector de arriba. */}
                En el último año, hasta {periodoAnual}, los alimentos en{' '}
                {selectedRow.place.name}{' '}
                {selectedRow.annual.percent < 0 ? 'bajaron' : 'subieron'}{' '}
                {percent(Math.abs(selectedRow.annual.percent))}, y en el país{' '}
                {nacionalAnual.percent < 0 ? 'bajaron' : 'subieron'}{' '}
                {percent(Math.abs(nacionalAnual.percent))}:{' '}
                {number(Math.abs(selectedRow.gapVsNational), 1)} puntos porcentuales de
                diferencia.
              </p>
            ) : null}
          </div>
        ) : null}
      </Sheet>
    </>
  )
}
